import { type AspectRatio, getAspectRatioStyle } from "@common/controls/AspectRatioContainer/lib";
import Icon from "@common/controls/Icon/component";
import ImageLoader from "@common/controls/Image/Loader/component";
import Preloader from "@common/controls/Preloader/component";
import ResponsiveContainer from "@common/controls/ResponsiveContainer/component";
import { Video } from "@common/controls/Video";
import BunnyVideo from "@common/controls/Video/Bunny/component";
import { IconType, type Media, type WithClassName } from "@common/declarations";
import { useI18n } from "@common/hooks/i18n";
import { usePromise } from "@common/lib/hooks";
import { getApi } from "@common/modules/system/api";
import classNames from "classnames";
import * as React from "react";
import { getMediaUrl } from "./lib";

export type CoverMode = "contain" | "cover";

export type ImageMediaUrlProps = {
  coverMode?: CoverMode;
  url: string;
};

export const ImageMediaUrl = (props: ImageMediaUrlProps) => <ImageLoader coverMode={props.coverMode} src={props.url} />;

export type MediaImageComponentProps = {
  aspectRatio?: AspectRatio;
  coverMode?: CoverMode;
  containerClassName?: string;
  containerStyle?: React.CSSProperties;
  noImageIcon?: IconType;
  overlayContent?: any;
  sourceKey?: Media.Source | null;
  sourceId?: string | null;
  url?: string | null;
};

export const MediaImageComponent = (props: MediaImageComponentProps) => (
  <ResponsiveContainer
    aspectRatio={props.aspectRatio}
    className={classNames("image_media", props.containerClassName)}
    style={props.containerStyle}
  >
    {props.url ? (
      <ImageMediaUrl coverMode={props.coverMode} url={props.url} />
    ) : props.sourceId && props.sourceKey ? (
      <ImageMediaUrl coverMode={props.coverMode} url={getMediaUrl(props.sourceKey, props.sourceId)} />
    ) : (
      <Icon icon={props.noImageIcon ?? IconType.Image} style={{ fontSize: `1em` }} />
    )}

    {!!props.overlayContent && <div className="overlay">{props.overlayContent}</div>}
  </ResponsiveContainer>
);

export type MediaComponentProps = Partial<WithClassName> & {
  aspectRatio?: AspectRatio;
  allowFullScreen?: boolean;
  bunnyLibraryId?: string;
  doesNotRequireVideoApp?: boolean;
  imageContainerStyle?: React.CSSProperties;
  shouldTrackVideo?: boolean;
  sourceKey: Media.Source;
  sourceId: string;
  typeKey: Media.Type;
};

export const MediaComponent = (props: MediaComponentProps) => {
  switch (props.typeKey) {
    case "Audio": {
      if (props.sourceKey === "SoundCloud") {
        return (
          <iframe className={props.className} src={props.sourceId} style={getAspectRatioStyle(props.aspectRatio)} />
        );
      }
      return <audio className={props.className} controls src={props.sourceId} />;
    }
    case "Image": {
      return (
        <MediaImageComponent
          aspectRatio={props.aspectRatio}
          containerStyle={props.imageContainerStyle}
          sourceKey={props.sourceKey}
          sourceId={props.sourceId}
        />
      );
    }
    case "Video": {
      switch (props.sourceKey) {
        case "Bunny": {
          return (
            <BunnyVideo
              allowFullScreen={props.allowFullScreen}
              aspectRatio={
                typeof props.aspectRatio === "string"
                  ? (props.aspectRatio.replace("/", ":") as `${number}:${number}`)
                  : "16:9"
              }
              isAlwaysAvailable={props.doesNotRequireVideoApp}
              libraryId={props.bunnyLibraryId}
              shouldTrack={props.shouldTrackVideo}
              videoId={props.sourceId}
            />
          );
        }
        case "URL": {
          return (
            <Video
              isAlwaysAvailable={props.doesNotRequireVideoApp}
              allowFullScreen={props.allowFullScreen}
              aspectRatio={props.aspectRatio ?? "16/9"}
              className={props.className}
              isBackground={false}
              showControls
              shouldTrack={props.shouldTrackVideo}
              url={props.sourceId}
            />
          );
        }
        default:
          return null;
      }
    }
    default:
      return null;
  }
};

export type DynamicMediaProps = Partial<WithClassName> & {
  aspectRatio?: AspectRatio;
  imageContainerStyle?: React.CSSProperties;
  id: string;
};

export const DynamicMedia = (props: DynamicMediaProps) => {
  const i18n = useI18n();

  const api = React.useMemo(() => getApi(i18n), [i18n]);

  const [media, isLoading] = usePromise<Media.Item>({
    fn: api.getMedia,
    args: [props.id],
    shouldLoad: true
  });

  if (isLoading) {
    if (props.aspectRatio) {
      return (
        <div style={{ ...getAspectRatioStyle(props.aspectRatio), ...props.imageContainerStyle }}>
          <Preloader isTransparent />
        </div>
      );
    }

    return <Preloader isTransparent />;
  }

  if (!media) return null;

  return (
    <MediaComponent
      aspectRatio={props.aspectRatio}
      className={props.className}
      imageContainerStyle={props.imageContainerStyle}
      sourceId={media.sourceId}
      sourceKey={media.mediaSourceKey}
      typeKey={media.mediaTypeKey}
    />
  );
};
